import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Flex } from 'foundations-components/components/Flex';
import CompanyLogo, { COMPANY_LOGO_USE } from 'MeetingsLib/components/decorative/CompanyLogo';
import BookingStepIndicator from 'MeetingsLib/components/sections/BookingStepIndicator';
import CompactBookingPage from 'MeetingsLib/components/page/CompactBookingPage';
import FullSizeBookingPage from 'MeetingsLib/components/page/FullSizeBookingPage';
import RescheduleHeader from 'MeetingsLib/components/page/RescheduleHeader';
import TroubleshootModeToggle from 'MeetingsLib/components/header/TroubleshootModeToggle';
import { canSubmitInitialPropertyForm as canSubmitInitialPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { getLegalConsentEnabled, getPaidMeetingsAvailableSelector, getRecaptchaEnabled, shouldShowEmailForm as shouldShowEmailFormSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAllRequiredConsentCollected, getLegalConsentResponses } from 'MeetingsLib/selectors/LegalConsentFormSelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { canTroubleshootSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import * as BookActions from 'MeetingsLib/redux/actions/BookActions';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import * as TroubleshootActions from 'MeetingsLib/redux/actions/TroubleshootActions';
import * as UIActions from 'MeetingsLib/redux/actions/UIActions';
import { BOOKING_STEPS } from 'MeetingsLib/constants/steps';
import { usePrefilledEmailValidation } from 'MeetingsLib/hooks/usePrefilledEmailValidation';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Booking = props => {
  const {
    avatar,
    compact,
    companyAvatar,
    bookingLifecycle,
    displayAvatarOption,
    emailFormState,
    formState,
    recaptchaEnabled,
    legalConsentEnabled,
    legalConsentResponses,
    legalConsentValid,
    branding,
    canSubmitInitialPropertyForm,
    shouldShowEmailForm,
    canTroubleshoot,
    embedded,
    headline,
    isFetchingPublicAvailability,
    isPaidMeeting,
    profile,
    welcomeScreenInfo,
    submittingMeeting,
    DatePickerMarker,
    TimePickerMarker,
    EmailFormMarker,
    isPreviewed,
    userMessage,
    hideLogo,
    isRescheduling,
    troubleshooting,
    toggleTroubleshootMode,
    fetchTroubleshootTimes,
    setContainerNodeRef
  } = props;
  const containerNodeRef = useRef(null);
  usePrefilledEmailValidation();
  useEffect(() => {
    if (containerNodeRef.current) {
      setContainerNodeRef(containerNodeRef.current);
    }
  }, [containerNodeRef, setContainerNodeRef]);
  const getCurrentStep = useCallback(() => {
    if (!bookingLifecycle.event.dateString || !bookingLifecycle.event.dateTime) {
      return BOOKING_STEPS.CHOOSE_TIME;
    }
    if (!formState || !legalConsentValid) {
      return BOOKING_STEPS.YOUR_INFO;
    }
    if (isPaidMeeting) {
      return BOOKING_STEPS.PAYMENT;
    }
    return null;
  }, [bookingLifecycle, formState, legalConsentValid, isPaidMeeting]);
  const BookingComponent = useMemo(() => compact ? CompactBookingPage : FullSizeBookingPage, [compact]);
  const renderedBookingInterface = useMemo(() => /*#__PURE__*/_jsx(BookingComponent, {
    avatar: avatar,
    companyAvatar: companyAvatar
    /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
    displayAvatarOption: displayAvatarOption,
    bookingLifecycle: bookingLifecycle,
    emailFormState: emailFormState,
    formState: formState,
    recaptchaEnabled: recaptchaEnabled,
    legalConsentEnabled: legalConsentEnabled,
    legalConsentResponses: legalConsentResponses,
    legalConsentValid: legalConsentValid,
    branding: branding,
    canSubmitInitialPropertyForm: canSubmitInitialPropertyForm,
    canTroubleshoot: canTroubleshoot,
    embedded: embedded,
    headline: headline,
    isFetchingPublicAvailability: isFetchingPublicAvailability,
    profile: profile,
    welcomeScreenInfo: welcomeScreenInfo,
    shouldShowEmailForm: shouldShowEmailForm,
    submittingMeeting: submittingMeeting,
    DatePickerMarker: DatePickerMarker,
    TimePickerMarker: TimePickerMarker,
    EmailFormMarker: EmailFormMarker
    /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
    currentStep: getCurrentStep(),
    isPaidMeeting: isPaidMeeting,
    isPreviewed: isPreviewed
  }), [avatar, companyAvatar, displayAvatarOption, bookingLifecycle, emailFormState, formState, recaptchaEnabled, legalConsentEnabled, legalConsentResponses, legalConsentValid, branding, canSubmitInitialPropertyForm, canTroubleshoot, embedded, headline, isFetchingPublicAvailability, profile, welcomeScreenInfo, shouldShowEmailForm, submittingMeeting, DatePickerMarker, TimePickerMarker, EmailFormMarker, getCurrentStep, isPaidMeeting, isPreviewed, BookingComponent]);
  const renderedPotentialAlert = useMemo(() => {
    return userMessage ? /*#__PURE__*/_jsx(View, {
      margin: "600",
      children: /*#__PURE__*/_jsx(Alert, {
        "data-selenium": "booking-alert-message",
        type: userMessage.type,
        children: userMessage.content
      })
    }) : null;
  }, [userMessage]);
  const renderedCompanyLogo = useMemo(() => {
    if (embedded || hideLogo) return null;
    return /*#__PURE__*/_jsx(CompanyLogo, {
      use: COMPANY_LOGO_USE.BOOKING_CONTAINER,
      compact: compact,
      branding: branding
    });
  }, [embedded, hideLogo, compact, branding]);
  const renderedRescheduleHeader = useMemo(() => isRescheduling && /*#__PURE__*/_jsx(RescheduleHeader, {}), [isRescheduling]);
  const renderedTroubleshootModeToggle = useMemo(() => !isRescheduling && canTroubleshoot && !compact ? /*#__PURE__*/_jsx(TroubleshootModeToggle, {
    troubleshooting: troubleshooting,
    toggleTroubleshootMode: toggleTroubleshootMode,
    fetchTroubleshootTimes: fetchTroubleshootTimes
  }) : null, [isRescheduling, canTroubleshoot, compact, troubleshooting, toggleTroubleshootMode, fetchTroubleshootTimes]);
  const needsBottomPadding = isPreviewed && compact;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!(shouldShowEmailForm && !emailFormState) && /*#__PURE__*/_jsx(BookingStepIndicator, {
      compact: compact
      /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
      currStep: getCurrentStep(),
      embedded: embedded,
      isPreviewed: isPreviewed,
      withPayment: isPaidMeeting
    }), /*#__PURE__*/_jsx("div", {
      className: "meetings-page",
      ref: containerNodeRef,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        paddingBlockEnd: needsBottomPadding ? '1200' : undefined,
        children: [renderedCompanyLogo, renderedRescheduleHeader, renderedTroubleshootModeToggle, renderedPotentialAlert, renderedBookingInterface]
      })
    })]
  });
};
const mapStateToProps = state => ({
  canSubmitInitialPropertyForm: canSubmitInitialPropertyFormSelector(state),
  canTroubleshoot: canTroubleshootSelector(state),
  embedded: state.embedded,
  eventDetails: state.eventDetails,
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  isSalesStarter: state.isSalesStarter,
  shouldShowEmailForm: shouldShowEmailFormSelector(state),
  emailFormState: state.emailFormState,
  troubleshooting: state.troubleshooting.active,
  userMessage: state.userMessage,
  bookingLifecycle: state.bookingLifecycle,
  formState: state.formState,
  legalConsentEnabled: getLegalConsentEnabled(state),
  recaptchaEnabled: getRecaptchaEnabled(state),
  legalConsentResponses: getLegalConsentResponses(state),
  legalConsentValid: getAllRequiredConsentCollected(state),
  isRescheduling: state.isRescheduling,
  isPaidMeeting: getPaidMeetingsAvailableSelector(state)
});
const mapDispatchToProps = Object.assign({}, BookActions, FormActions, UIActions, TroubleshootActions);
export default connect(mapStateToProps, mapDispatchToProps)(Booking);